import * as React from "react"

import { cn } from "@/lib/utils"

/*
   A stat is either INERT or it is a FILTER, and the two must not look
   alike. `onSelect` separates them: present ⇒ pressable button (filter),
   absent ⇒ inert div (summary). An inert summary must not invite a click
   that does nothing.
*/

export type StatTone = "neutral" | "err" | "print" | "master" | "info" | "brand" | "people"

const TONE_TEXT: Record<StatTone, string> = {
  neutral: "text-text",
  err: "text-err",
  print: "text-print",
  master: "text-master",
  info: "text-info",
  brand: "text-brand",
  people: "text-people",
}

const TONE_BG: Record<StatTone, string> = {
  neutral: "bg-text",
  err: "bg-err",
  print: "bg-print",
  master: "bg-master",
  info: "bg-info",
  brand: "bg-brand",
  people: "bg-people",
}

export interface StatCardProps {
  /** The number, or a short phrase like "Non-notification". */
  value: React.ReactNode
  label: React.ReactNode
  tone?: StatTone
  /** Muted when the value is zero — an empty count should not shout. */
  dimWhenZero?: boolean
  /** Present ⇒ pressable filter. Absent ⇒ inert summary. */
  onSelect?: () => void
  selected?: boolean
  disabled?: boolean
  /** Six bars, newest last. Omit for no trend. */
  spark?: number[]
  className?: string
}

export const StatCard = React.forwardRef<HTMLDivElement | HTMLButtonElement, StatCardProps>(
  function StatCard(
    {
      value,
      label,
      tone = "neutral",
      dimWhenZero = true,
      onSelect,
      selected = false,
      disabled = false,
      spark,
      className,
    },
    ref,
  ) {
    const zero = value === 0 || value === "0"
    const body = (
      <>
        <div className="flex items-baseline gap-2">
          <span
            className={cn(
              "font-id text-[26px] leading-none tracking-[-0.02em]",
              zero && dimWhenZero ? "text-text-disabled" : TONE_TEXT[tone],
            )}
          >
            {value}
          </span>
          {selected && (
            <span className="font-id text-micro uppercase tracking-[0.1em] text-text-3">
              filtering
            </span>
          )}
          {spark && <Spark values={spark} tone={tone} className="ml-auto" />}
        </div>
        <div className="mt-1.5 text-caption leading-tight text-text-3">{label}</div>
      </>
    )

    const shell = cn(
      "rounded-lg border bg-surface px-3.5 py-3 text-left",
      "transition-colors duration-120 ease-out",
      className,
    )

    if (!onSelect) {
      return (
        <div ref={ref as React.Ref<HTMLDivElement>} className={cn(shell, "border-border")}>
          {body}
        </div>
      )
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        aria-pressed={selected}
        disabled={disabled}
        onClick={onSelect}
        className={cn(
          shell,
          selected ? "border-text shadow-e1" : "border-border hover:border-border-strong",
          "disabled:pointer-events-none disabled:opacity-45",
        )}
      >
        {body}
      </button>
    )
  },
)

function Spark({
  values,
  tone,
  className,
}: {
  values: number[]
  tone: StatTone
  className?: string
}) {
  const max = Math.max(...values, 1)
  return (
    <span aria-hidden="true" className={cn("flex h-5 items-end gap-0.5", className)}>
      {values.map((v, i) => (
        <span
          key={i}
          className={cn(
            "w-0.5 rounded-[1px]",
            i === values.length - 1 ? TONE_BG[tone] : "bg-border",
          )}
          style={{ height: `${Math.max(2, (v / max) * 20)}px` }}
        />
      ))}
    </span>
  )
}

/*
   `mod` renders ⌘ on Apple platforms and Ctrl elsewhere, so the hint
   matches the reader's machine rather than the author's.
*/

const isApple = () =>
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)

const GLYPH: Record<string, string> = {
  mod: "⌘",
  shift: "⇧",
  alt: "⌥",
  enter: "⏎",
  esc: "Esc",
  up: "↑",
  down: "↓",
  left: "←",
  right: "→",
  pageup: "⇞",
  pagedown: "⇟",
}

export interface KbdProps extends React.ComponentPropsWithoutRef<"kbd"> {
  /** "mod" resolves per platform. Others map through GLYPH, else verbatim. */
  keys: string | string[]
  /** For the dark document surround. */
  variant?: "light" | "dark"
}

export const Kbd = React.forwardRef<HTMLSpanElement, KbdProps>(function Kbd(
  { className, keys, variant = "light", ...props },
  ref,
) {
  const list = Array.isArray(keys) ? keys : [keys]
  const [apple, setApple] = React.useState(true)

  // resolved after mount so SSR and the client agree on first paint
  React.useEffect(() => {
    setApple(isApple())
  }, [])

  const render = (k: string) => {
    const key = k.toLowerCase()
    if (key === "mod") return apple ? "⌘" : "Ctrl"
    if (key === "alt") return apple ? "⌥" : "Alt"
    return GLYPH[key] ?? k
  }

  return (
    <span ref={ref} className="inline-flex items-center gap-0.5">
      {list.map((k, i) => (
        <kbd
          key={i}
          className={cn(
            "inline-flex min-w-4.5 items-center justify-center rounded-xs px-1.5 py-px",
            "font-id text-micro leading-normal",
            variant === "light"
              ? "border border-border bg-surface text-text-2"
              : "border border-white/25 bg-transparent text-white/80",
            className,
          )}
          {...props}
        >
          {render(k)}
        </kbd>
      ))}
    </span>
  )
})

export function ShortcutHint({
  keys,
  children,
  variant = "light",
  className,
}: {
  keys: string | string[]
  children: React.ReactNode
  variant?: "light" | "dark"
  className?: string
}) {
  return (
    <span className={cn("inline-flex items-center gap-1.5", className)}>
      <Kbd keys={keys} variant={variant} />
      <span
        className={cn(
          "text-caption",
          variant === "light" ? "text-text-3" : "text-white/60",
        )}
      >
        {children}
      </span>
    </span>
  )
}
